export type PractitionerPracticeMode = 'licensed' | 'wellness';
export type ServiceTrack = 'clinical' | 'wellness';
export type ComputedPracticeMode = 'licensed' | 'wellness' | 'dual';

export interface PractitionerModeMeta {
  practice_mode?: string | null;
  license_status?: string | null;
  subscription_status?: string | null;
  availability_rules?: any;
}

export function hasActiveSubscription(status?: string | null): boolean {
  return status === 'active' || status === 'trialing';
}

export interface ServiceTrackInfo {
  service_track: ServiceTrack;
  is_active: boolean;
}

export function isLicensedPractitioner(mode?: string | null): boolean {
  return mode === 'licensed';
}

export function computePracticeMode(services: ServiceTrackInfo[]): ComputedPracticeMode {
  const activeServices = services.filter(s => s.is_active);
  const hasClinical = activeServices.some(s => s.service_track === 'clinical');
  const hasWellness = activeServices.some(s => s.service_track === 'wellness');

  if (hasClinical && hasWellness) return 'dual';
  if (hasClinical) return 'licensed';
  return 'wellness';
}

export function hasConfiguredAvailability(availabilityRules: any): boolean {
  if (!availabilityRules || typeof availabilityRules !== 'object') {
    return false;
  }

  const weeklySchedule = availabilityRules.weekly_schedule;
  if (weeklySchedule && typeof weeklySchedule === 'object') {
    const hasWeekly = Object.values(weeklySchedule).some((day: any) => {
      if (!day?.enabled) return false;
      return Array.isArray(day.slots) && day.slots.some((slot: any) => slot?.start && slot?.end);
    });
    if (hasWeekly) return true;
  }

  const customDates = availabilityRules.custom_dates;
  if (Array.isArray(customDates)) {
    const hasCustom = customDates.some(
      (entry: any) => Array.isArray(entry?.slots) && entry.slots.some((slot: any) => slot?.start && slot?.end)
    );
    if (hasCustom) return true;
  }

  return false;
}

/**
 * Clinical keyword guard for wellness services.
 * Returns list of detected clinical keywords.
 */
const CLINICAL_KEYWORDS = [
  'clinical', 'diagnosis', 'prescription', 'licensed therapy',
  'psychotherapy', 'psychiatric', 'medical treatment', 'dsm',
  'mental disorder', 'psychological assessment',
  '临床', '诊断', '处方', '精神科', '心理治疗师'
];

export function detectClinicalKeywords(text: string): string[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  return CLINICAL_KEYWORDS.filter(kw => lower.includes(kw.toLowerCase()));
}

export function canPractitionerBeListedPublic(
  practitioner: PractitionerModeMeta,
  activeServiceCount: number
): boolean {
  if (activeServiceCount <= 0) return false;

  if (!isLicensedPractitioner(practitioner.practice_mode)) {
    return true;
  }

  const hasApprovedLicense = practitioner.license_status === 'approved';
  const hasAvailability = hasConfiguredAvailability(practitioner.availability_rules);
  const hasSubscription = hasActiveSubscription(practitioner.subscription_status);

  return hasApprovedLicense && hasAvailability && hasSubscription;
}
